import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';

import FaviconPng from '../../assets/images/captivision/favicon.png';
import AppleTouchIcon from '../../assets/images/captivision/apple-touch-icon.jpg';
import ClientIcon from '../../assets/images/captivision/captivision-camera.jpg';
import HostIcon from '../../assets/images/captivision/captivision-host.jpg';

import Favicon from '../../components/favicon/favicon.component';

import { APP, BASE, HOME, NAV } from './captivision.config';
import { LANGUAGES, findLanguage } from './i18n/languages';
import { fillText, useCVLanguage } from './i18n/language.component';
import { STORE_IMAGES } from './i18n/store-images';
import { SCREENS } from './screens';

// The chrome every CaptiVision page shares: the tab icon, the sticky top bar,
// the footer, the light/dark switch and the language menu, always in the
// reader's language. Kept here rather than copied into each page so the nav
// only has to be corrected in one place.

// the product's own app icon in the browser tab, for as long as a CaptiVision
// page is on screen. public/index.html ships the same icon.
export const CVFavicon = () => (
    <Favicon icon={FaviconPng} apple={AppleTouchIcon} type="image/png" />
)

// The site ships dark and remembers a switch to light. The attribute lives on
// <html> rather than on the page wrapper so the choice survives moving between
// CaptiVision pages; every CaptiVision rule is scoped to .captivision.
const THEME_KEY = 'captivision-site-theme';

const readTheme = () => {
    try {
        return localStorage.getItem(THEME_KEY) === 'light' ? 'light' : 'dark'
    } catch (error) {
        // private browsing, fall back to the shipped theme
        return 'dark'
    }
}

export const useCaptiVisionTheme = () => {
    const [theme, setTheme] = useState(readTheme);

    useEffect(() => {
        document.documentElement.setAttribute('data-theme', theme);

        return () => document.documentElement.removeAttribute('data-theme')
    }, [theme])

    const toggle = useCallback(() => {
        setTheme(current => {
            const next = current === 'light' ? 'dark' : 'light';

            try {
                localStorage.setItem(THEME_KEY, next);
            } catch (error) {
                // nothing to remember it with, the switch still works for this visit
            }

            return next
        })
    }, [])

    return [theme, toggle]
}

// The top bar is sticky, so an anchor landing under it would hide its own
// heading. Everything on the page scrolls to this offset instead.
const SCROLL_OFFSET = 88;

export const scrollToId = id => {
    const element = document.getElementById(id);

    if (!element) {
        return
    }

    window.scrollTo({
        top: element.getBoundingClientRect().top + window.pageYOffset - SCROLL_OFFSET,
        behavior: 'smooth'
    })
}

// Scrolls to whatever `#section` the url arrived with, once the page is laid
// out. Used by the landing page, which is what the cross-page nav links target.
export const useHashScroll = hash => {
    useEffect(() => {
        if (!hash) {
            return
        }

        const id = hash.replace('#', '');
        const timer = setTimeout(() => scrollToId(id), 0);

        return () => clearTimeout(timer)
    }, [hash])
}

const MoonIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 12.8A9 9 0 1111.2 3a7 7 0 009.8 9.8z" />
    </svg>
)

const SunIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </svg>
)

const GlobeIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9" />
        <path d="M3.5 9h17M3.5 15h17M12 3c-2.4 2.5-3.6 5.5-3.6 9s1.2 6.5 3.6 9M12 3c2.4 2.5 3.6 5.5 3.6 9s-1.2 6.5-3.6 9" />
    </svg>
)

const CheckIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M5 12.5l4.5 4.5L19 7.5" />
    </svg>
)

const MenuIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round">
        <path d="M4 7h16M4 12h16M4 17h16" />
    </svg>
)

// the two apps, as the landing and pricing pages offer them for download
export const CV_APPS = [
    { key: 'client', name: 'CaptiVision Client', icon: ClientIcon, href: APP.clientAppStoreUrl },
    { key: 'host', name: 'CaptiVision Host', icon: HostIcon, href: APP.hostAppStoreUrl }
]

// Apple's official "Download on the App Store" badge in the reader's language,
// used unaltered: white on the dark theme, black on the light one. `app` is
// one of CV_APPS.
export const CVAppStoreBadge = ({ app }) => {
    const { code, t } = useCVLanguage();
    const badge = STORE_IMAGES[code].badge;
    const alt = fillText(t.home.apps.badge, { app: app.name });

    return (
        <a className="cv-app-store" href={app.href}>
            <img className="cv-badge-white" src={badge.white} alt={alt}/>
            <img className="cv-badge-black" src={badge.black} alt={alt}/>
        </a>
    )
}

export const CVBrand = () => (
    <Link className="cv-brand" to={HOME}>
        <img src={HostIcon} alt="" loading="eager" />
        <span> {APP.name} </span>
    </Link>
)

// The language menu, beside the light/dark switch. Every language is listed in
// its own name and script, with its name in the language being read underneath
// where the browser knows it. Arrow keys move through the list, Escape closes it.
const CVLanguageMenu = () => {
    const { code, name, t, choose } = useCVLanguage();
    const [open, setOpen] = useState(false);
    const wrap = useRef(null);
    const button = useRef(null);
    const items = useRef([]);

    const nameIn = useMemo(() => {
        try {
            const names = new Intl.DisplayNames([code], { type: 'language' });

            return other => names.of(other)
        } catch (error) {
            // an older browser: the menu shows each language's own name only
            return () => null
        }
    }, [code])

    useEffect(() => {
        if (!open) {
            return
        }

        const checked = items.current[LANGUAGES.findIndex(language => language.code === code)];

        if (checked) {
            checked.focus();
        }

        const closeOutside = event => {
            if (wrap.current && !wrap.current.contains(event.target)) {
                setOpen(false);
            }
        }

        document.addEventListener('pointerdown', closeOutside);

        return () => document.removeEventListener('pointerdown', closeOutside)
    }, [open, code])

    const close = () => {
        setOpen(false);
        button.current.focus();
    }

    const pick = next => {
        choose(next);
        close();
    }

    const moveFocus = event => {
        const count = LANGUAGES.length;
        const index = items.current.indexOf(document.activeElement);
        const to = {
            ArrowDown: (index + 1) % count,
            ArrowUp: (index - 1 + count) % count,
            Home: 0,
            End: count - 1
        }[event.key];

        if (event.key === 'Escape') {
            event.preventDefault();
            close();
        } else if (event.key === 'Tab') {
            setOpen(false);
        } else if (to !== undefined) {
            event.preventDefault();
            items.current[to].focus();
        }
    }

    return (
        <div className="cv-lang" ref={wrap} onKeyDown={open ? moveFocus : undefined}>
            <button
                ref={button}
                className="cv-btn cv-btn-icon"
                type="button"
                aria-haspopup="menu"
                aria-expanded={open}
                aria-controls="cv-lang-menu"
                aria-label={`${t.topbar.language}: ${name}`}
                title={t.topbar.language}
                onClick={() => setOpen(current => !current)}>
                <GlobeIcon />
            </button>
            {open ? (
                <div className="cv-lang-menu" id="cv-lang-menu" role="menu" aria-label={t.topbar.languageMenu}>
                    {LANGUAGES.map((language, index) => {
                        const checked = language.code === code;
                        const local = checked ? null : nameIn(language.code);

                        return (
                            <button
                                key={language.code}
                                ref={element => { items.current[index] = element }}
                                type="button"
                                role="menuitemradio"
                                aria-checked={checked}
                                tabIndex={-1}
                                onClick={() => pick(language.code)}>
                                {/* lang only: a dir would push Arabic to the far edge of the list */}
                                <span lang={language.code}> {language.name} </span>
                                {local && local !== language.name ? <small> {local} </small> : null}
                                {checked ? <CheckIcon /> : null}
                            </button>
                        )
                    })}
                </div>
            ) : null}
        </div>
    )
}

// `current` marks which nav entry is the page we are on, matching the
// aria-current the static site used.
export const CVTopbar = ({ theme, onToggleTheme, current, wide = false, extra = null }) => {
    const history = useHistory();
    const location = useLocation();
    const { t } = useCVLanguage();
    const [menuOpen, setMenuOpen] = useState(false);

    const followNav = (link, event) => {
        setMenuOpen(false);

        if (!link.hash) {
            return
        }

        // a section of the landing page: scroll to it when we are already there,
        // otherwise route home and let the landing page pick the hash up
        event.preventDefault();

        if (location.pathname === link.to) {
            scrollToId(link.hash);
        } else {
            history.push(`${link.to}#${link.hash}`);
        }
    }

    return (
        <header className={`cv-topbar${wide ? ' cv-topbar-wide' : ''}`}>
            <div className="cv-topbar-in">
                <CVBrand />
                <nav className={`cv-nav${menuOpen ? ' is-open' : ''}`} aria-label={t.topbar.mainNav}>
                    {NAV.map(link => (
                        <Link
                            key={link.key}
                            to={link.hash ? `${link.to}#${link.hash}` : link.to}
                            aria-current={current === link.label ? 'page' : undefined}
                            onClick={event => followNav(link, event)}>
                            {t.nav[link.key]}
                        </Link>
                    ))}
                </nav>
                <div className="cv-tools">
                    {extra}
                    <CVLanguageMenu />
                    <button
                        className="cv-btn cv-btn-icon"
                        type="button"
                        onClick={onToggleTheme}
                        aria-label={theme === 'light' ? t.topbar.toDark : t.topbar.toLight}>
                        {theme === 'light' ? <SunIcon /> : <MoonIcon />}
                    </button>
                    <Link className="cv-btn cv-btn-accent cv-btn-sm cv-get-started" to={`${BASE}/get-started`}> {t.topbar.getStarted} </Link>
                    <button
                        className="cv-btn cv-btn-icon cv-menu-btn"
                        type="button"
                        aria-label={t.topbar.menu}
                        aria-expanded={menuOpen}
                        onClick={() => setMenuOpen(open => !open)}>
                        <MenuIcon />
                    </button>
                </div>
            </div>
        </header>
    )
}

// A page whose content is in English while the reader chose another language
// (the tester instructions, or a page with no translation yet) says so, in the
// reader's language, under the top bar. `lang` is the language the content is in.
export const CVEnglishOnly = ({ lang }) => {
    const { code, t } = useCVLanguage();

    return lang && lang !== code ? <p className="cv-english-only"> {t.englishOnly} </p> : null
}

// lang and dir for a page's content, which is in `lang`
export const contentLanguage = lang => ({ lang, dir: (findLanguage(lang) || {}).dir || 'ltr' })

// `note` is a line of small print under the footer, such as the landing page
// saying its screenshots are simulated
export const CVFooter = ({ current, wide = false, note = null }) => {
    const { t } = useCVLanguage();

    // `label` is the English name pages pass as `current`
    const links = [
        { key: 'getStarted', label: 'Get started', to: `${BASE}/get-started` },
        { key: 'guide', label: 'User guide', to: `${BASE}/guide` },
        { key: 'story', label: 'Our story', to: `${BASE}/story` },
        { key: 'privacy', label: 'Privacy', to: `${BASE}/privacy` },
        { key: 'terms', label: 'Terms of Use', to: `${BASE}/terms` }
    ]

    return (
        <footer className={`cv-footer${wide ? ' cv-footer-wide' : ''}`}>
            <div className="cv-footer-in">
                <CVBrand />
                <nav aria-label={t.footer.label}>
                    {links.map(link => (
                        <Link key={link.key} to={link.to} aria-current={current === link.label ? 'page' : undefined}>
                            {t.footer[link.key]}
                        </Link>
                    ))}
                    {/* <a href={APP.companyUrl}> Synergie Tech </a> */}
                </nav>
                <span> &copy; {APP.copyrightYear} Synergie Tech </span>
            </div>
            {note ? <p className="cv-footer-note"> {note} </p> : null}
        </footer>
    )
}

// The two guide pages sit under one sub-navigation, shown under the page title.
export const CVSubnav = ({ current }) => {
    const { t } = useCVLanguage();

    return (
        <nav className="cv-subnav" aria-label={t.docs.subnav}>
            <Link to={`${BASE}/get-started`} aria-current={current === 'get-started' ? 'page' : undefined}> {t.docs.getStarted} </Link>
            <Link to={`${BASE}/guide`} aria-current={current === 'guide' ? 'page' : undefined}> {t.docs.guide} </Link>
        </nav>
    )
}

// the "back to / next" pair at the foot of every document page
export const CVNext = ({ back, next }) => (
    <div className="cv-next">
        {back
            ? <Link to={back.to}> <small> {back.kicker} </small> <span> {back.label} </span> </Link>
            : <span />}
        {next
            ? <Link to={next.to} className="cv-next-right"> <small> {next.kicker} </small> <span> {next.label} </span> </Link>
            : null}
    </div>
)

// One screenshot. It links to the full-size image, so a reader can open a
// small guide figure at full resolution. `solo` centres a lone phone shot.
export const CVShot = ({ screen, caption, solo = false, lazy = true }) => (
    <figure className={`cv-shot${screen.phone ? ' cv-shot-phone' : ''}${solo ? ' cv-shot-solo' : ''}`}>
        <a href={screen.src}>
            <img src={screen.src} width={screen.width} height={screen.height} alt={screen.alt} loading={lazy ? 'lazy' : 'eager'} />
        </a>
        {caption ? <figcaption> {caption} </figcaption> : null}
    </figure>
)

// One of the app screenshots in screens.js, by name, in the reader's language:
// outside English, the App Store's screenshot for that language, described by
// the strings file. Takes CVShot's other props.
export const useScreen = name => {
    const { code, t } = useCVLanguage();
    const store = STORE_IMAGES[code].screens[name];

    return store ? { ...store, alt: t.screens[name] } : SCREENS[name]
}

export const Shot = ({ name, ...props }) => <CVShot screen={useScreen(name)} {...props} />

// a row of screenshots; `layout` is 'pair', 'phones', 3 or 4
export const CVFigs = ({ layout, children }) => (
    <div className={`cv-figs cv-figs-${layout}`}>
        {children}
    </div>
)
